import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface EscrowAccountItem {
  description: string;
  monthlyAmount: number;
}

export interface EscrowAccountData {
  hasEscrow: boolean;
  monthlyEscrowPayment?: number;
  items?: EscrowAccountItem[];
  initialEscrowPaymentAtClosing?: number;
  nonEscrowPropertyCosts?: number;
  nonEscrowPropertyCostsPeriod?: string;
}

export interface CDEscrowAccountProps {
  /** Escrow account data */
  data: Partial<EscrowAccountData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<EscrowAccountData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDEscrowAccount - Escrow Account details section on Page 4
 *
 * Shows escrow payment breakdown or no-escrow cost estimate.
 */
export const CDEscrowAccount = React.forwardRef<HTMLDivElement, CDEscrowAccountProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update top-level fields
    const updateField = (field: keyof EscrowAccountData, value: number | string | boolean) => {
      onChange?.({ ...data, [field]: value });
    };

    // Helper to update escrow items
    const updateItem = (itemIndex: number, updates: Partial<EscrowAccountItem>) => {
      const items = data.items || [];
      const updatedItems = items.map((item, idx) =>
        idx === itemIndex ? { ...item, ...updates } : item,
      );
      onChange?.({ ...data, items: updatedItems });
    };

    if (data.hasEscrow) {
      return (
        <div ref={ref} className={cn('', className)}>
          <CDSectionHeader title="Escrow Account" />

          <div className="border border-t-0 border-cd-black p-2">
            <div className="grid grid-cols-2 gap-3">
              {/* Left - Monthly Payment */}
              <div>
                <div className="cd-text-small font-bold mb-1">
                  Escrowed Property Costs over Year 1
                </div>
                <div className="cd-text-tiny">
                  Estimated total amount over year 1 for your escrowed property costs:
                </div>
                <div className="text-[12pt] font-bold mt-1">
                  <CDCurrency
                    value={data.monthlyEscrowPayment ? data.monthlyEscrowPayment * 12 : undefined}
                    editable={editable}
                    onChange={(v) => updateField('monthlyEscrowPayment', v / 12)}
                  />
                </div>

                {data.items && data.items.length > 0 && (
                  <div className="mt-2">
                    <div className="cd-text-tiny font-bold mb-1">Escrowed Property Costs</div>
                    <table className="w-full cd-text-tiny">
                      <tbody>
                        {data.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-0.5">
                              <CDEditableText
                                value={item.description}
                                editable={editable}
                                onChange={(v) => updateItem(idx, { description: v })}
                                size="tiny"
                              />
                            </td>
                            <td className="py-0.5 text-right w-16">
                              <CDCurrency
                                value={item.monthlyAmount}
                                editable={editable}
                                onChange={(v) => updateItem(idx, { monthlyAmount: v })}
                                className="cd-text-tiny"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Right - Additional Info */}
              <div>
                <div className="cd-text-small font-bold mb-1">
                  Non-Escrowed Property Costs over Year 1
                </div>
                <div className="cd-text-tiny">
                  Estimated total amount over year 1. You must pay these costs directly, possibly in
                  one or two large payments a year.
                </div>
                <div className="text-[12pt] font-bold mt-1">
                  <CDCurrency
                    value={data.nonEscrowPropertyCosts}
                    editable={editable}
                    onChange={(v) => updateField('nonEscrowPropertyCosts', v)}
                  />
                </div>

                <div className="cd-text-small font-bold mt-3 mb-1">Initial Escrow Payment</div>
                <div className="cd-text-tiny">
                  A cushion for the escrow account you pay at closing. See Section G on page 2.
                </div>
                <div className="text-[12pt] font-bold mt-1">
                  <CDCurrency
                    value={data.initialEscrowPaymentAtClosing}
                    editable={editable}
                    onChange={(v) => updateField('initialEscrowPaymentAtClosing', v)}
                  />
                </div>

                <div className="cd-text-small font-bold mt-3 mb-1">Monthly Escrow Payment</div>
                <div className="cd-text-tiny">
                  The amount included in your total monthly payment.
                </div>
                <div className="text-[12pt] font-bold mt-1">
                  <CDCurrency
                    value={data.monthlyEscrowPayment}
                    editable={editable}
                    onChange={(v) => updateField('monthlyEscrowPayment', v)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No Escrow version
    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="No Escrow" />

        <div className="border border-t-0 border-cd-black p-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="cd-text-small font-bold mb-1">
                Estimated Property Costs over Year 1
              </div>
              <div className="text-[12pt] font-bold">
                <CDCurrency
                  value={data.nonEscrowPropertyCosts}
                  editable={editable}
                  onChange={(v) => updateField('nonEscrowPropertyCosts', v)}
                />
              </div>
              <div className="cd-text-tiny mt-1">
                Estimated amount over year 1 for property taxes, homeowner's insurance, and other
                property costs.
              </div>
            </div>
            <div>
              <div className="cd-text-tiny">
                You must pay these costs directly, possibly in one or two large payments a year.
                Contact your lender to ask if escrow is available.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDEscrowAccount.displayName = 'CDEscrowAccount';
